import * as React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import { Grid } from "semantic-ui-react";

import { Detail } from "@neworbit/simpleui-detail";

import { ClinicianSelector } from "../../components/ClinicianSelector";
import { MobileClinicDetails, AddUnknownEmployeeModel, MatchingEmployeeModel } from "../models";
import { WorkGroup, loadWorkGroups as loadWorkGroupsAction } from "../../../crud/workGroup";
import { ApplicationState } from "../../../applicationState";
import { addUnknownEmployeeToClinic, searchForEmployeeFromCustomer } from "../actions";
import { ADD_UNKNOWN_EMPLOYEE_MODEL } from "../actionTypes";

import { AddUnknownEmployeeModal } from "./AddUnknownEmployeeModal";
import { MobileClinicAppointments } from "./MobileClinicAppointments";

interface OwnProps {
    clinic: MobileClinicDetails;
    teamPreventUsers: { id: string; name: string }[];
    conflicts: string[] | false | undefined;
    onCliniciansChanged: (clinicians: { id: string; name: string }[]) => void;
}

interface StateProps {
    allWorkGroups: WorkGroup[];
}

interface DispatchProps {
    loadWorkGroups: (customerId: string) => Promise<void>;
    searchForEmployee: (clinicId: string, employeeName: string) => Promise<MatchingEmployeeModel[]>;
    addUnknownEmployee: (clinicId: string, model: AddUnknownEmployeeModel) => Promise<void>;
}

type Props = OwnProps & StateProps & DispatchProps;

const MobileClinicEditUnconnected = (props: Props) => {
    const { clinic, teamPreventUsers, conflicts, onCliniciansChanged, allWorkGroups, loadWorkGroups, addUnknownEmployee, searchForEmployee } = props;
    const workGroups = React.useMemo(() => allWorkGroups.filter(wg => wg.customerId === clinic.customer.id), [allWorkGroups, clinic.customer.id]);

    React.useEffect(() => {
        if (!workGroups.length && clinic.customer) {
            loadWorkGroups(clinic.customer.id);
        }
    }, [clinic.customer, clinic.customer.id, workGroups.length, loadWorkGroups]);

    const onAddUnknownEmployee = React.useCallback((model: AddUnknownEmployeeModel) => addUnknownEmployee(clinic.id, model), [clinic.id, addUnknownEmployee]);

    const onSearchForEmployee = React.useCallback((employeeName: string) => searchForEmployee(clinic.id, employeeName), [clinic.id, searchForEmployee]);

    return (
        <Grid>
            <Detail label="Mobile Unit" value={clinic && clinic.mobileUnit} />
            <Detail label="Address" value={clinic && clinic.address} />
            <Detail label="Start" value={clinic && clinic.start.format("DD-MM-YYYY HH:mm")} />
            <Detail label="End" value={clinic && clinic.end.format("DD-MM-YYYY HH:mm")} />
            <Detail label="Customer" value={clinic && clinic.customer?.name} />
            <Detail label="Clinicians Required" value={clinic && clinic.cliniciansRequired.toString()} />
            <ClinicianSelector
                restrictToOneClinician
                selectedClinicians={teamPreventUsers}
                highlight={{ start: clinic && clinic.start, end: clinic && clinic.end }}
                conflicts={conflicts}
                onCliniciansChanged={onCliniciansChanged}
            />
            <Grid.Row>
                <Grid.Column>
                    <MobileClinicAppointments
                        clinic={clinic}
                    />
                    <AddUnknownEmployeeModal
                        searchForEmployee={onSearchForEmployee}
                        onSubmit={onAddUnknownEmployee}
                        workGroups={workGroups}
                        saveRequestNames={[ADD_UNKNOWN_EMPLOYEE_MODEL]}
                        customerId={clinic.customer?.id}
                        clinicId={clinic.id}
                    />
                </Grid.Column>
            </Grid.Row>
        </Grid>);
};

const mapStateToProps = (state: ApplicationState): StateProps => ({
    allWorkGroups: state.workGroups,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, any>): DispatchProps => ({
    loadWorkGroups: (customerId) => dispatch<Promise<void>>(loadWorkGroupsAction({ customerId })),
    searchForEmployee: (clinicId, employeeName) => dispatch<Promise<any>>(searchForEmployeeFromCustomer(clinicId, employeeName)),
    addUnknownEmployee: (clinicId, model) => dispatch<Promise<any>>(addUnknownEmployeeToClinic(clinicId, model))
});

export const MobileClinicEdit = connect(mapStateToProps, mapDispatchToProps)(MobileClinicEditUnconnected);
